import { IAppContext } from 'App/AppContext';
import { mapFromAsset, mapFromAssetContext, mapFromAssets, mapToAsset } from 'mappings/assetMapping';
import { mapFromAssetsFromTemplatesResult } from 'mappings/assetTemplateMapping';
import Asset from 'models/asset/asset';
import { AssetContext } from 'models/asset/assetContext';
import { AssetsFromTemplatesResult } from 'models/assetsFromTemplates';
import { AssetContextDTO } from 'models/dto/asset/assetContextDTO';
import AssetDTO, { AssetProcessDTO } from 'models/dto/asset/assetDTO';
import EntityRelationDTO from 'models/dto/entityRelationDTO';
import IdListDTO from 'models/dto/IdListDTO';
import { AssetsFromTemplatesDTO, AssetsFromTemplatesResultDTO } from 'models/dto/assetsFromTemplatesDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetAsset(id: number, accessToken: string, cache: GlobalDataCache): Promise<Asset | undefined> {
  try {
    if (!id || id <= 0) return undefined;
    const ar = await http.get<AssetDTO>(`/assets/${id}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromAsset(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAssets(accessToken: string, cache: GlobalDataCache): Promise<Asset[]> {
  try {
    const ar = await http.get<AssetDTO[]>(`/assets`, http.getRequestConfig(accessToken));

    return mapFromAssets(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAssetChildren(id: number, accessToken: string): Promise<number[]> {
  try {
    const ar = await http.get<IdListDTO>(`/assets/${id}/children`, http.getRequestConfig(accessToken));
    if (!ar.data.idList) return [];

    return ar.data.idList.map((a) => Number.parseInt(a));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAssetCode(accessToken: string, code: string): Promise<string> {
  try {
    const ar = await http.post<string>(`/assets/code`, { valueString: code }, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAssetExternalId(accessToken: string, externalId: string): Promise<number> {
  try {
    const ar = await http.post<number>(
      `/assets/externalid`,
      { valueString: externalId },
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddAsset(
  newAsset: Asset,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Asset> {
  try {
    const assetDTO: AssetDTO = mapToAsset(newAsset);

    const ar = await http.post<AssetDTO>(`/assets`, assetDTO, http.getRequestConfig(accessToken));

    return mapFromAsset(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * "Add from catalogue" (PLAN-073, spec 3.3): one asset per template, with the template's type and its name and
 * description in every language of the organization. A template whose name is already an asset's is skipped and named
 * in the result; at most 100 ids per call (ASSET0003).
 */
export async function apiAddAssetsFromTemplates(
  templateIds: number[],
  accessToken: string,
): Promise<AssetsFromTemplatesResult> {
  try {
    const ar = await http.post<AssetsFromTemplatesResultDTO>(
      `/assets/fromtemplates`,
      new AssetsFromTemplatesDTO(templateIds),
      http.getRequestConfig(accessToken),
    );

    return mapFromAssetsFromTemplatesResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateAsset(
  editAsset: Asset,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Asset> {
  try {
    const assetDTO: AssetDTO = mapToAsset(editAsset);

    const ar = await http.put<AssetDTO>(`/assets`, assetDTO, http.getRequestConfig(accessToken));

    return mapFromAsset(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveAsset(asset: Asset, accessToken: string): Promise<boolean> {
  try {
    await http.post(`/assets/delete/${asset.assetId}`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export const apiGetAssetContext = async (
  assetId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<AssetContext> => {
  try {
    const ar = await http.get<AssetContextDTO>(`/assets/context/${assetId}`, http.getRequestConfig(accessToken));

    return mapFromAssetContext(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export async function apiAddAssetToAsset(assetId: number, assetIdTo: number, accessToken: string): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = assetId;
    body.idTo = assetIdTo;
    await http.post<EntityRelationDTO>(`/assets/asset?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveAssetFromAsset(
  assetId: number,
  assetIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = assetId;
    body.idTo = assetIdTo;
    await http.post<EntityRelationDTO>(`/assets/asset?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Process
export async function apiAddProcessToAsset(
  assetId: number,
  processIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new AssetProcessDTO(assetId, processIdTo);
    await http.post<AssetProcessDTO>(`/assets/process?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveProcessFromAsset(
  assetId: number,
  processIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new AssetProcessDTO(assetId, processIdTo);
    await http.post<AssetProcessDTO>(`/assets/process?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAssetsForRisk(
  riskId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Asset[]> {
  try {
    const ar = await http.get<AssetDTO[]>(`/assets/risk/${riskId}`, http.getRequestConfig(accessToken));

    return mapFromAssets(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddClassificationInheritance(
  assetId: number,
  containerId: number,
  classificationId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    await http.put(
      `/assets/inheritance/asset/${assetId}/container/${containerId}/classification/${classificationId}?add=${true}`,
      null,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveClassificationInheritance(
  assetId: number,
  containerId: number,
  classificationId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    await http.put(
      `/assets/inheritance/asset/${assetId}/container/${containerId}/classification/${classificationId}?add=${false}`,
      null,
      http.getRequestConfig(accessToken),
    );

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAssetsContainersOnly(accessToken: string, cache: GlobalDataCache): Promise<Asset[]> {
  try {
    const ar = await http.get<AssetDTO[]>(`/assets/containers`, http.getRequestConfig(accessToken));

    return mapFromAssets(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
